/**
 * Order arithmetic for the views rail.
 *
 * The rail renders one ownership group as several independent sortable lists --
 * one per folder, plus the loose views at the bottom -- but `view/order` is a
 * statement about the *whole* ownership group. `v-sortable` only ever reports
 * the list the drag happened in, so the two have to be reconciled somewhere,
 * and this module is that somewhere.
 */

/**
 * Splices a reordered sublist back into the group's full ordered id list.
 *
 * A view is dragged inside exactly one list: a single folder's contents, or the
 * loose views. Posting that list on its own would be read as "these are the
 * views, in this order, and nothing else" -- and both the frontend's
 * `ORDER_ITEMS` mutation and the backend's `View.order_objects` give every
 * omitted id an order of `0`. One drag among the loose views therefore
 * flattened the order of every view inside every folder, which the user saw as
 * their folders silently reshuffling.
 *
 * The fix is positional: the sublist's members keep the *slots* they already
 * occupied in the full order and are redistributed across those slots in their
 * new relative order. Every id outside the sublist keeps its slot untouched.
 *
 * @param {Array<number>} fullOrder Every view id in the ownership group, in
 *   its current order.
 * @param {Array<number>} sublistOrder The dragged list's ids in their new
 *   order. Must be a subset of `fullOrder`.
 * @return {Array<number>} `fullOrder` with the sublist's slots refilled.
 */
export function mergeSublistOrder(fullOrder, sublistOrder) {
  // The sublist is filtered against the full order first, so an id that is no
  // longer in the group -- a view another session deleted while this one was
  // mid-drag -- cannot be written back into the order and resurrect a view that
  // does not exist. The result is always a permutation of `fullOrder`.
  const present = new Set(fullOrder)
  const usable = sublistOrder.filter((id) => present.has(id))
  const sublist = new Set(usable)
  let next = 0
  return fullOrder.map((id) => (sublist.has(id) ? usable[next++] : id))
}
